import React, { useState, useMemo, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Checkbox,
    Autocomplete,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { TEAM_HEADS } from '../../constants/hrConstants';
import { GET_BRANCHES } from '../../api';

const EmployeeSelectionModal = ({ open, onClose, employees, selectedEmployees, onToggle, onSelectAll, onClearAll }) => {
    const [modalSearch, setModalSearch] = useState('');
    const [modalBranch, setModalBranch] = useState(null);
    const [modalTeamHead, setModalTeamHead] = useState(null);
    const [branchOptions, setBranchOptions] = useState([]);

    useEffect(() => {
        GET_BRANCHES()
            .then((data) => setBranchOptions((data || []).map((b) => b.name)))
            .catch(() => setBranchOptions([]));
    }, []);

    const filtered = useMemo(() => {
        return employees.filter(emp => {
            const matchesSearch = modalSearch ?
                (emp.name.toLowerCase().includes(modalSearch.toLowerCase()) ||
                    emp.employee_no.toLowerCase().includes(modalSearch.toLowerCase())) : true;
            const matchesBranch = modalBranch ? emp.branch_name === modalBranch : true;
            const matchesTeamHead = modalTeamHead ? emp.team_head === modalTeamHead : true;
            return matchesSearch && matchesBranch && matchesTeamHead;
        });
    }, [employees, modalSearch, modalBranch, modalTeamHead]);

    const allFilteredSelected = filtered.length > 0 && filtered.every(f => selectedEmployees.some(s => s.id === f.id));

    const handleToggleAllFiltered = () => {
        if (allFilteredSelected) {
            // Deselect all in this filtered list
            filtered.forEach(f => {
                if (selectedEmployees.some(s => s.id === f.id)) onToggle(f);
            });
        } else {
            // Select all in this filtered list
            filtered.forEach(f => {
                if (!selectedEmployees.some(s => s.id === f.id)) onToggle(f);
            });
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 700 },
                maxHeight: '85vh',
                bgcolor: 'background.paper',
                borderRadius: '24px',
                boxShadow: 24,
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
                    <Typography variant="h6" fontWeight={700}>Advanced Employee Selection</Typography>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
                </Box>

                {/* Filters */}
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#f8f9fa' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name or Employee ID..."
                        value={modalSearch}
                        onChange={(e) => setModalSearch(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} /> }}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Autocomplete
                            size="small"
                            options={TEAM_HEADS}
                            value={modalTeamHead}
                            onChange={(_, v) => setModalTeamHead(v)}
                            renderInput={(p) => <TextField {...p} label="Team Head" />}
                        />
                        <Autocomplete
                            size="small"
                            options={branchOptions}
                            value={modalBranch}
                            onChange={(_, v) => setModalBranch(v)}
                            renderInput={(p) => <TextField {...p} label="Branch" />}
                        />
                    </Box>
                </Box>

                <Divider />

                {/* List Actions */}
                <Box sx={{ px: 3, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox checked={allFilteredSelected} onChange={handleToggleAllFiltered} size="small" />
                        <Typography variant="body2" fontWeight={600}>Select Filtered ({filtered.length})</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {selectedEmployees.length} total selected
                    </Typography>
                </Box>

                {/* List */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1, bgcolor: '#fff' }}>
                    <List dense>
                        {filtered.map((employee) => {
                            const isSelected = selectedEmployees.some((e) => e.id === employee.id);
                            return (
                                <ListItem key={employee.id} disablePadding>
                                    <ListItemButton onClick={() => onToggle(employee)} sx={{ borderRadius: '8px' }}>
                                        <Checkbox checked={isSelected} size="small" sx={{ '&.Mui-checked': { color: '#667eea' } }} />
                                        <ListItemAvatar>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: isSelected ? '#667eea' : '#ccc' }}>
                                                {employee.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography variant="body2" fontWeight={isSelected ? 700 : 500}>{employee.name}</Typography>}
                                            secondary={<Typography variant="caption">{employee.employee_no} | {employee.designation}</Typography>}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #e9ecef' }}>
                    <Button variant="text" onClick={onClearAll} sx={{ color: 'text.secondary', textTransform: 'none' }}>Clear All Selected</Button>
                    <Button variant="contained" onClick={onClose} sx={{ px: 4, borderRadius: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none' }}>Apply Selection</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EmployeeSelectionModal;
